// The home page's motion (ported from the concept site's inline script): the rotating word
// in the headline with its slide + progress pills (each pill picks its slide), the sample
// dialog the example cards and the hero's image open, the reveal-on-scroll, the nav's "form
// away" state; plus the hamburger menu, which every page carries (web/site/_nav.html). Each
// part guards on its markup, so the one bundle serves every page.

const WORDS = ["product", "promo", "training", "support", "release", "launch", "onboarding"];
const WORD_COLORS = [
  "var(--blue)",
  "var(--red)",
  "var(--violet)",
  "var(--green)",
  "var(--blue)",
  "var(--orange)",
  "var(--pink)",
];
const ROTATE_MS = 4800;
const SWAP_MS = 540;

// One clip per example card, in the cards' order; the hero's slides play one per word, in
// HERO_SAMPLE_SLUGS' order. Posters live under /static/landing: `<slug>.jpg` is the full 1920×1080 frame (the
// hero's slides and the dialog's poster), `<slug>.thumb.jpg` the 800×450 cut for the 174 px
// card thumbs. The clips (1080p, 10–45 MB, faststart) are direct links into the public
// bucket (docs/project/deployment-architecture.md): a plain <video src> needs no CORS there.
const SAMPLE_SLUGS = [
  "bittermuse",
  "booking",
  "reschedule",
  "spend-insights",
  "first-visit",
  "school-year",
  "appointment",
  "onboarding",
  "sales",
  "registration",
  "treatment",
  "customer-story",
];
const HERO_SAMPLE_SLUGS = [
  "bittermuse",
  "appointment",
  "school-year",
  "reschedule",
  "spend-insights",
  "registration",
  "onboarding",
];
const VIDEO_BASE = "https://featurette-public.s3.us-east-2.amazonaws.com/samples/";
const POSTER_BASE = "/static/landing/";

const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;

/** The hamburger menu (every page). */
export function mobileMenu(): void {
  const button = document.getElementById("mobileMenuToggle");
  const menu = document.getElementById("mobileMenu");
  if (!button || !menu) return;
  const set = (open: boolean) => {
    menu.hidden = !open;
    button.setAttribute("aria-expanded", String(open));
    button.setAttribute("aria-label", open ? "Close menu" : "Open menu");
  };
  button.addEventListener("click", () => set(button.getAttribute("aria-expanded") !== "true"));
  for (const link of menu.querySelectorAll("a")) link.addEventListener("click", () => set(false));
  document.addEventListener("pointerdown", (e) => {
    const t = e.target as Node | null;
    if (!menu.hidden && t && !menu.contains(t) && !button.contains(t)) set(false);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !menu.hidden) {
      set(false);
      button.focus();
    }
  });
}

/** The home page: headline word cycle + slides, the sample dialog, reveal, the nav's form state.
 * The cycle runs whatever the reduced-motion setting, like most sites' carousels; the pills pick
 * a slide either way. */
export function hero(): void {
  const line = document.getElementById("animatedLine");
  const slot = document.getElementById("wordSlot");
  const progress = document.getElementById("heroProgress");
  const dialog = document.getElementById("sampleDialog");
  const stage = document.getElementById("dialogStage");
  const dialogTitle = document.getElementById("dialogTitle");
  const dialogType = document.getElementById("dialogType");
  const heroPlay = document.getElementById("heroPlay");
  let current = document.getElementById("rotatingWord");
  if (
    !line ||
    !slot ||
    !progress ||
    !current ||
    !(dialog instanceof HTMLDialogElement) ||
    !stage ||
    !dialogTitle ||
    !dialogType ||
    !heroPlay
  ) {
    return;
  }
  const slides = [...document.querySelectorAll<HTMLElement>(".hero-slide")];
  const pills = [...progress.querySelectorAll<HTMLButtonElement>("button")];
  const bars = [...progress.querySelectorAll<HTMLElement>("span")];

  // ---------- the word cycle ----------
  let wordIndex = 0;
  let rotationTimer = 0;
  let cleanupTimer = 0;
  let frameA = 0;
  let frameB = 0;
  const measure = (el: Element) => Math.ceil(el.getBoundingClientRect().width);
  const fitWord = () => {
    if (current) slot.style.width = `${measure(current)}px`;
  };
  const stopCycle = () => {
    clearTimeout(rotationTimer);
    clearTimeout(cleanupTimer);
    cancelAnimationFrame(frameA);
    cancelAnimationFrame(frameB);
    rotationTimer = cleanupTimer = frameA = frameB = 0;
  };
  const showIndex = () => {
    line.style.color = WORD_COLORS[wordIndex] ?? "";
    line.setAttribute("aria-label", `${WORDS[wordIndex]} videos.`);
    slides.forEach((slide, i) => {
      slide.classList.toggle("active", i === wordIndex);
    });
    bars.forEach((bar, i) => {
      bar.classList.remove("active");
      bar.classList.toggle("done", i < wordIndex);
    });
    pills.forEach((pill, i) => {
      if (i === wordIndex) pill.setAttribute("aria-current", "true");
      else pill.removeAttribute("aria-current");
    });
    void progress.offsetWidth; // restart the bar's animation
    bars[wordIndex]?.classList.add("active");
  };
  const paintWord = () => {
    stopCycle();
    const word = document.createElement("span");
    word.className = "rotating-word";
    word.id = "rotatingWord";
    word.textContent = WORDS[wordIndex] ?? "";
    slot.replaceChildren(word);
    current = word;
    showIndex();
    fitWord();
  };
  const scheduleCycle = () => {
    clearTimeout(rotationTimer);
    if (!document.hidden) rotationTimer = window.setTimeout(rotateWord, ROTATE_MS);
  };
  const rotateWord = () => {
    if (document.hidden || !current) return;
    const previous = current;
    wordIndex = (wordIndex + 1) % WORDS.length;
    const next = document.createElement("span");
    next.className = "rotating-word enter";
    next.textContent = WORDS[wordIndex] ?? "";
    slot.appendChild(next);
    slot.style.width = `${measure(next)}px`;
    showIndex();
    frameA = requestAnimationFrame(() => {
      frameB = requestAnimationFrame(() => {
        previous.classList.add("exit");
        next.classList.remove("enter");
      });
    });
    cleanupTimer = window.setTimeout(() => {
      previous.remove();
      current = next;
      cleanupTimer = 0;
      scheduleCycle();
    }, SWAP_MS);
  };
  const restoreCycle = () => {
    paintWord();
    scheduleCycle();
  };
  void (document.fonts?.ready ?? Promise.resolve()).then(restoreCycle);
  for (const [i, pill] of pills.entries()) {
    pill.addEventListener("click", () => {
      wordIndex = i;
      restoreCycle();
    });
  }
  addEventListener("resize", fitWord);
  addEventListener("pagehide", paintWord);
  addEventListener("pageshow", restoreCycle);
  document.addEventListener("visibilitychange", () =>
    document.hidden ? paintWord() : restoreCycle(),
  );

  // ---------- the nav's form state + the shortcuts into the form ----------
  const siteNav = document.getElementById("siteNav");
  const form = document.getElementById("make");
  const siteUrl = document.getElementById("siteUrl");
  if (siteNav && form) {
    new IntersectionObserver(
      ([entry]) => siteNav.classList.toggle("form-away", !entry?.isIntersecting),
      { threshold: 0.08, rootMargin: "-86px 0px 0px" },
    ).observe(form);
  }
  for (const link of document.querySelectorAll(".focus-form,.form-shortcut")) {
    link.addEventListener("click", () =>
      setTimeout(() => siteUrl?.focus({ preventScroll: true }), reduced ? 0 : 520),
    );
  }

  // ---------- the sample dialog ----------
  let lastTrigger: HTMLElement | null = null;
  const openSample = (slug: string, title: string, type: string, trigger: HTMLElement) => {
    lastTrigger = trigger;
    const video = document.createElement("video");
    video.controls = true;
    video.playsInline = true;
    video.preload = "metadata";
    video.poster = `${POSTER_BASE}${slug}.jpg`;
    video.src = `${VIDEO_BASE}${slug}.mp4`;
    video.setAttribute("aria-label", `${title} sample video`);
    stage.replaceChildren(video);
    dialogTitle.textContent = title;
    dialogType.textContent = `· ${type}`;
    stopCycle();
    dialog.showModal();
    document.body.classList.add("modal-open");
    video.play().catch(() => {});
  };
  const cards = [...document.querySelectorAll<HTMLElement>(".example-card")];
  cards.forEach((card, i) => {
    const slug = SAMPLE_SLUGS[i];
    const thumb = card.querySelector(".thumb");
    if (!slug || !thumb) return;
    const poster = document.createElement("img");
    poster.src = `${POSTER_BASE}${slug}.thumb.jpg`;
    poster.alt = "";
    poster.loading = "lazy";
    thumb.replaceChildren(poster);
    card.addEventListener("click", () =>
      openSample(slug, card.dataset.title ?? "", card.dataset.type ?? "", card),
    );
  });
  const playSlide = () => {
    const slug = HERO_SAMPLE_SLUGS[wordIndex];
    const card = cards[SAMPLE_SLUGS.indexOf(slug ?? "")];
    if (slug && card) openSample(slug, card.dataset.title ?? "", card.dataset.type ?? "", heroPlay);
  };
  // The button is the keyboard's way in; a pointer can click anywhere on the image.
  heroPlay.addEventListener("click", playSlide);
  document.querySelector(".hero-slides")?.addEventListener("click", (e) => {
    if (!(e.target as Element).closest(".hero-play,.hero-progress")) playSlide();
  });
  document.getElementById("dialogClose")?.addEventListener("click", () => dialog.close());
  dialog.addEventListener("click", (e) => {
    if (e.target === dialog) dialog.close();
  });
  dialog.addEventListener("close", () => {
    const video = stage.querySelector("video");
    if (video) {
      video.pause();
      video.removeAttribute("src");
      video.load();
    }
    stage.replaceChildren();
    document.body.classList.remove("modal-open");
    lastTrigger?.focus({ preventScroll: true });
    restoreCycle();
  });

  // ---------- reveal on scroll ----------
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) if (entry.isIntersecting) entry.target.classList.add("visible");
    },
    { threshold: 0.1 },
  );
  for (const el of document.querySelectorAll(".reveal")) io.observe(el);
}
